import React, { useState } from 'react';
import { X, Truck, Building2, Phone, Mail, FileText, Plus, User, CreditCard, Landmark, CircleDollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import fournisseurService from '../../services/fournisseurService';
import './AddFournisseurDialog.css';

const AddFournisseurDialog = ({ isOpen, onClose, onFournisseurAdded }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    nif: '',
    address: '',
    contact_email: '',
    contact_email_secondary: '',
    contact_phone: '',
    iban: '',
    bank_account_number: '',
    bank_beneficiary: '',
    devise: 'EUR',
    devise_custom: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError(t('fournisseurs.dialog.errors.nameRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Construire le payload avec la devise
      const { devise_custom, ...rest } = formData;
      const payload = {
        ...rest,
        devise: formData.devise === 'AUTRE' && devise_custom 
          ? devise_custom.toUpperCase() 
          : formData.devise
      };
      
      console.log('Payload envoyé:', payload); // DEBUG
      
      const response = await fournisseurService.create(payload);
      console.log('Réponse backend:', response); // DEBUG
      onFournisseurAdded(response);
      resetForm();
      onClose();
    } catch (err) {
      console.error('Erreur création fournisseur:', err.response?.data); // DEBUG
      setError(err.response?.data?.detail || t('fournisseurs.dialog.errors.create'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_name: '',
      nif: '',
      address: '',
      contact_email: '',
      contact_email_secondary: '',
      contact_phone: '',
      iban: '',
      bank_account_number: '',
      bank_beneficiary: '',
      devise: 'EUR',
      devise_custom: '',
      notes: ''
    });
    setError(null);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <div className="dialog-overlay ui-dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-container ui-dialog-container">
        <div className="dialog-header ui-dialog-header">
          <div className="dialog-title ui-dialog-title">
            <Truck size={24} />
            <h2>{t('fournisseurs.dialog.addTitle')}</h2>
          </div>
          <button className="dialog-close" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="dialog-error">
            {error}
          </div>
        )}

        <form className="dialog-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name" className="ui-label">
              <Building2 size={16} />
              {t('fournisseurs.dialog.form.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('fournisseurs.dialog.form.namePlaceholder')}
              required
              className="ui-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact_name" className="ui-label">
                <User size={16} />
                {t('fournisseurs.dialog.form.contactName')}
              </label>
              <input
                type="text"
                id="contact_name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                placeholder={t('fournisseurs.dialog.form.contactNamePlaceholder')}
                className="ui-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="nif" className="ui-label">
                <FileText size={16} />
                {t('fournisseurs.dialog.form.nif')}
              </label>
              <input
                type="text"
                id="nif"
                name="nif"
                value={formData.nif}
                onChange={handleChange}
                placeholder={t('fournisseurs.dialog.form.nifPlaceholder')}
                className="ui-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address" className="ui-label">
              <FileText size={16} />
              {t('fournisseurs.dialog.form.address')}
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder={t('fournisseurs.dialog.form.addressPlaceholder')}
              rows={2}
              className="ui-textarea"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact_email" className="ui-label">
                <Mail size={16} />
                {t('fournisseurs.dialog.form.email')}
              </label>
              <input
                type="email"
                id="contact_email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder={t('fournisseurs.dialog.form.emailPlaceholder')}
                className="ui-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contact_email_secondary" className="ui-label">
                <Mail size={16} />
                {t('fournisseurs.dialog.form.emailSecondary')}
              </label>
              <input
                type="email"
                id="contact_email_secondary"
                name="contact_email_secondary"
                value={formData.contact_email_secondary}
                onChange={handleChange}
                placeholder={t('fournisseurs.dialog.form.emailSecondaryPlaceholder')}
                className="ui-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contact_phone" className="ui-label">
              <Phone size={16} />
              {t('fournisseurs.dialog.form.phone')}
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              placeholder={t('fournisseurs.dialog.form.phonePlaceholder')}
              className="ui-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="iban" className="ui-label">
                <CreditCard size={16} />
                {t('fournisseurs.dialog.form.iban')}
              </label>
              <input
                type="text"
                id="iban"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                placeholder={t('fournisseurs.dialog.form.ibanPlaceholder')}
                className="ui-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bank_account_number" className="ui-label">
                <Landmark size={16} />
                {t('fournisseurs.dialog.form.bankAccountNumber')}
              </label>
              <input
                type="text"
                id="bank_account_number"
                name="bank_account_number"
                value={formData.bank_account_number}
                onChange={handleChange}
                placeholder={t('fournisseurs.dialog.form.bankAccountNumberPlaceholder')}
                className="ui-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="bank_beneficiary" className="ui-label">
                <Landmark size={16} />
                {t('fournisseurs.dialog.form.bankBeneficiary')}
              </label>
              <input
                type="text"
                id="bank_beneficiary"
                name="bank_beneficiary"
                value={formData.bank_beneficiary}
                onChange={handleChange}
                placeholder={t('fournisseurs.dialog.form.bankBeneficiaryPlaceholder')}
                className="ui-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="devise" className="ui-label">
                <CircleDollarSign size={16} />
                {t('fournisseurs.dialog.form.devise')}
              </label>
              <select
                id="devise"
                name="devise"
                value={formData.devise}
                onChange={handleChange}
                className="form-select ui-select"
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="MAD">MAD (DH)</option>
                <option value="GBP">GBP (£)</option>
                <option value="AOA">AOA (Kz)</option>
                <option value="AUTRE">{t('fournisseurs.dialog.form.deviseOther')}</option>
              </select>
            </div>
          </div>

          {formData.devise === 'AUTRE' && (
            <div className="form-group">
              <label htmlFor="devise_custom" className="ui-label">
                <CircleDollarSign size={16} />
                {t('fournisseurs.dialog.form.deviseCustom')}
              </label>
              <input
                type="text"
                id="devise_custom"
                name="devise_custom"
                value={formData.devise_custom}
                onChange={handleChange}
                placeholder={t('fournisseurs.dialog.form.deviseCustomPlaceholder')}
                maxLength={3}
                className="ui-input"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="notes" className="ui-label">
              <FileText size={16} />
              {t('fournisseurs.dialog.form.notes')}
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder={t('fournisseurs.dialog.form.notesPlaceholder')}
              rows={2}
              className="ui-textarea"
            />
          </div>

          <div className="form-notice">
            <Mail size={16} />
            <span>{t('fournisseurs.dialog.form.userCreationNotice')}</span>
          </div>

          <div className="dialog-actions ui-dialog-actions">
            <button
              type="button"
              className="btn-secondary ui-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary ui-btn-primary"
              disabled={loading}
            >
              {loading ? t('common.saving') : (
                <>
                  <Plus size={18} />
                  {t('fournisseurs.dialog.actions.create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFournisseurDialog;
